import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useTranslation } from './useI18n';
import { useMenuItems, type MenuItem } from './useMenuItems';

export interface Breadcrumb {
  title: string;
  to?: any;
  disabled: boolean;
}

const findPath = (
  items: MenuItem[],
  currentRouteName: string | symbol | null | undefined,
  path: MenuItem[] = []
): MenuItem[] | null => {
  for (const item of items) {
    const newPath = [...path, item];
    if (item.name === currentRouteName) {
      return newPath;
    }
    if (item.children && item.children.length) {
      const found = findPath(item.children, currentRouteName, newPath);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

export function useBreadcrumbs() {
  const route = useRoute();
  const t = useTranslation();
  const { menuItems } = useMenuItems();

  const breadcrumbs = computed(() => {
    const path = findPath(menuItems.value, route.name);

    // If we can't find a path, or the path is for a top-level item,
    // don't show any breadcrumbs. They are only useful for nested pages.
    if (!path || path.length < 2) {
      return [];
    }

    const breadcrumbs: Breadcrumb[] = [];

    if (path) {
      path.forEach((item, index) => {
        breadcrumbs.push({
          title: item.title,
          to: item.name ? { name: item.name } : undefined,
          disabled: index === path.length - 1,
        });
      });
    }

    return breadcrumbs;
  });

  return { breadcrumbs };
}
